/* Built In Imports */
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';

/* External Imports */
import {
  Box,
  Button,
  Center,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Image,
  Input,
  Link,
} from '@chakra-ui/react';
import { render } from 'datocms-structured-text-to-html-string';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';

/* Internal Imports */
/* Components */
import SelectUI from '@components/Elements/SelectUI';
import StructuredTextParser from '@components/Utility/ArticleParser/StructuredTextParser';
import { phoneWithCountryCode } from '@constants/phoneWithCountryCode';
import { MomHeading } from '../Sliders/MeditationSlider';

/* Services */
import { mixPanel } from '@utils/Mixpanel';
import { useFormImpression } from '@utils/globalMixpanelEvents';
import { getCurrentRegion, pushLeadEnquiry } from 'services/commonService';

/**
 * Renders the MomForm component
 *
 * @param {object} section
 * @returns {ReactElement} MomForm component.
 */
const MomForm = ({ section }) => {
  const searchParams = useSearchParams();
  const [isLoading, setLoading] = useState(false);
  const [showResp, setShowResp] = useState(false);
  const [curCountry, setCurCountry] = useState('');
  const [curIpCountry, setCurIpCountry] = useState('');
  const router = useRouter();
  const Mixpanel = mixPanel(router.asPath);
  useFormImpression('mom-form', 'mom_lead_form_impression');

  const localData = async () => {
    const reg = await getCurrentRegion();

    if (reg?.country) {
      setCurCountry(reg?.country);
      setCurIpCountry(reg?.country);
    }
  };

  useEffect(() => {
    localData();
    Object.keys(phoneWithCountryCode).forEach(function (key, index) {
      phoneWithCountryCode[key]['label'] = `${
        phoneWithCountryCode[key]['iso2_code']
      } + (${phoneWithCountryCode[key]['phone_code']?.replace('+', '')})`;
      phoneWithCountryCode[key]['value'] =
        phoneWithCountryCode[key]['iso2_code'];
    });
  }, []);

  // phoneWithCountryCode.sort(function (a, b) {
  //   return a.label - b.label;
  // });

  // console.log('newObjectnewObjectnewObject', phoneWithCountryCode);

  const initialVal = {
    fName: '',
    lName: '',
    email: '',
    phoneCountryCode: '',
    mobile: '',
    privacy: false,
  };

  const ValidationSchema = Yup.object().shape({
    fName: Yup.string()
      .matches(/^([-a-zA-Z ])+$/u, {
        message: 'First Name is invalid',
      })
      .max(100, 'First Name is invalid')
      .required('First Name is required'),
    lName: Yup.string()
      .matches(/^([-a-zA-Z ])+$/u, {
        message: 'Last Name is invalid',
      })
      .max(100, 'Last Name is invalid')
      .required('Last Name is required'),
    email: Yup.string()
      .required('Email is required')
      .matches(/^[a-zA-Z]+[a-zA-Z0-9._]+@[a-zA-Z]+(\.[a-zA-Z]{2,15})+$/, {
        message: 'Email is invalid',
      }),
    phoneCountryCode: Yup.string(),
    // .min(1, 'Invalid phone code')
    // .max(5, 'Invalid phone code')
    // .required('Phone code is required'),
    mobile: Yup.string()
      .min(curCountry === 'IN' ? 10 : 6, 'Mobile number is invalid')
      .max(curCountry === 'IN' ? 10 : 16, 'Mobile number is invalid')
      .required('Mobile number is required'),
    // .when('phoneCountryCode', {
    //   is: 'IN',
    //   then: Yup.string().matches(/^\d{10}$/, 'Mobile number is invalid'),
    //   otherwise: Yup.string().matches(
    //     /^\d{6,16}$/,
    //     'Mobile number is invalid'
    //   ),
    // }),
    privacy: Yup.boolean()
      .oneOf([true], 'Please select Privacy Policy and Terms and Conditions')
      .required('Please select Privacy Policy and Terms and Conditions'),
  });

  const onSubmit = async (values, { setSubmitting, resetForm }) => {
    // console.log('onSubmit', searchParams);
    // const reg = await getCurrentRegion(searchParams.get('country'));

    const { fName, lName, email, phoneCountryCode, mobile } = values;

    const phoneCountryOption =
      phoneWithCountryCode.find(item => item.iso2_code === curCountry)
        ?.phone_code || '';
    // console.log(
    //   'phoneCountryOption',
    //   phoneCountryOption,
    //   phoneCountryCode,
    //   curCountry
    // );

    setLoading(true);
    const leadsData = {
      message: {
        systemId: '110',
        programType: 'MoM Beta',
        name: `${fName} ${lName}`,
        phone: `${phoneCountryOption} ${mobile}`,
        whatsapp: '',
        email: email || '',
        jobTitle: '',
        companyName: '',
        country: curCountry,
        utmCampaign: searchParams.get('utmCampaign'),
        utmMedium: searchParams.get('utmMedium'),
        utmSource: searchParams.get('utmSource'),
        utmTerm: searchParams.get('utmTerm'),
        utmReferer: searchParams.get('utmReferer'),
        programYear: new Date().getFullYear(),
      },
    };
    // console.log('leadsData', leadsData);
    pushLeadEnquiry(leadsData);
    setLoading(false);
    setShowResp(true);
    Mixpanel.track('mom_lead_form_submit', {});
  };

  const ref = useRef(null);
  // console.log('section=>', section);
  const fieldName = section[0]?.data;

  return (
    <Flex
      w={{ base: '', sm: '', md: '' }}
      flexFlow={{ base: 'column-reverse', md: 'row' }}
      flexWrap="wrap"
      pos="relative"
      margin={{ base: '0px 0 0', md: '25px 20px 0' }}
      justifyContent="center"
      padding="0 0 20px 0"
    >
      {!showResp && curCountry && (
        <Formik
          initialValues={initialVal}
          validationSchema={ValidationSchema}
          onSubmit={onSubmit}
        >
          {({
            values,
            handleChange,
            handleSubmit,
            setFieldValue,
            setFieldTouched,
            setTouched,
            errors,
            isSubmitting,
            isValid,
            dirty,
          }) => (
            <Form onSubmit={handleSubmit}>
              <Box
                display="flex"
                gap={{ base: '5px', lg: '20px' }}
                flexWrap="wrap"
                justifyContent="space-between"
                maxW={{ base: 'auto', md: '644px' }}
                mx={{ base: '15px', lg: 'auto' }}
              >
                <Center
                  flexDir="column"
                  display="flex"
                  justifyContent="center"
                  textAlign="center"
                  mx="auto"
                  w="100%"
                  pt={{ base: '40px', md: '70px' }}
                  pb="20px"
                >
                  <MomHeading
                    maxW="none"
                    text={fieldName?.heading || 'Be the First to Experience'}
                  />
                </Center>
                <Field name="fName">
                  {({ field, form }) => (
                    <FormControl
                      isRequired
                      isInvalid={form.errors.fName && form.touched.fName}
                      width={{ base: '99%', lg: '48%' }}
                      m={{ base: '0 0 18px 0', xl: '0' }}
                    >
                      <FormLabel
                        fontFamily="'Geist',sans-serif"
                        fontSize="16px"
                      >
                        {fieldName.fName}
                      </FormLabel>
                      <Input
                        {...field}
                        id="fName"
                        variant="outline"
                        placeholder={fieldName.fName}
                        fontFamily="'Geist',sans-serif"
                        fontSize="18px"
                        color="#28231E"
                        height={{ base: '48px', lg: '60px' }}
                        padding="10px"
                        borderRadius="8px"
                        border="1px solid #D6D8D9"
                        p="20px"
                        mr={{ sm: '20px' }}
                      />
                      <FormErrorMessage fontFamily="'Geist',sans-serif">
                        {form.errors.fName}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <Field name="lName">
                  {({ field, form }) => (
                    <FormControl
                      isRequired
                      isInvalid={form.errors.lName && form.touched.lName}
                      width={{ base: '99%', lg: '48%' }}
                      m={{ base: '0 0 18px 0', xl: '0' }}
                    >
                      <FormLabel
                        fontFamily="'Geist',sans-serif"
                        fontSize="16px"
                      >
                        {fieldName.lName}
                      </FormLabel>
                      <Input
                        {...field}
                        id="lName"
                        variant="outline"
                        placeholder={fieldName.lName}
                        fontFamily="'Geist',sans-serif"
                        fontSize="18px"
                        color="#28231E"
                        height={{ base: '48px', lg: '60px' }}
                        padding="10px"
                        borderRadius="8px"
                        border="1px solid #D6D8D9"
                        p="20px"
                        mr={{ sm: '20px' }}
                      />
                      <FormErrorMessage fontFamily="'Geist',sans-serif">
                        {form.errors.lName}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <Field name="email">
                  {({ field, form }) => (
                    <FormControl
                      isRequired
                      isInvalid={form.errors.email && form.touched.email}
                      width={{ base: '99%', lg: '48%' }}
                      m={{ base: '0 0 18px 0', xl: '0' }}
                    >
                      <FormLabel
                        fontFamily="'Geist',sans-serif"
                        fontSize="16px"
                      >
                        {fieldName.email}
                      </FormLabel>
                      <Input
                        {...field}
                        id="email"
                        variant="outline"
                        placeholder={fieldName.email}
                        fontFamily="'Geist',sans-serif"
                        fontSize="18px"
                        color="#28231E"
                        height={{ base: '48px', lg: '60px' }}
                        padding="10px"
                        borderRadius="8px"
                        border="1px solid #D6D8D9"
                        p="20px"
                        type="email"
                      />
                      <FormErrorMessage fontFamily="'Geist',sans-serif">
                        {form.errors.email}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <Box
                  display="flex"
                  w={{ base: '100%', lg: '48%' }}
                  position="relative"
                >
                  <FormLabel
                    fontFamily="'Geist',sans-serif"
                    fontSize="16px"
                    position="absolute"
                    left="0px"
                  >
                    {fieldName.mobile}{' '}
                    <Box as="span" color="#e53e3e">
                      *
                    </Box>
                  </FormLabel>
                  <Field name="phoneCountryCode">
                    {({ field, form }) => (
                      <FormControl
                        isRequired
                        isInvalid={
                          form.errors.phoneCountryCode &&
                          form.touched.phoneCountryCode
                        }
                        // width={{ base: '50%', lg: '45%' }}
                        m={{ base: '0 0 18px 0', xl: '0px' }}
                        w="120px"
                      >
                        <SelectUI
                          field={field}
                          // placeholder="+91"
                          defaultValue={curCountry}
                          id="phoneCountryCode"
                          options={phoneWithCountryCode}
                          onChange={event => {
                            handleChange(event);
                            setCurCountry(event.target.value);
                          }}
                          value={curCountry}
                          fontFamily="'Geist',sans-serif"
                          fontSize="16px"
                          height={{ base: '48px', lg: '60px' }}
                          border="1px solid #D6D8D9"
                          borderRightColor="transparent"
                          borderRadius="8px  0px 0px 8px"
                          mt="32px"
                          isDisabled={curIpCountry === 'IN'}
                          style={{ padding: '0 5px' }}
                        />
                        <FormErrorMessage fontFamily="'Geist',sans-serif">
                          {form.errors.phoneCountryCode}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="mobile">
                    {({ field, form }) => (
                      <FormControl
                        isRequired
                        isInvalid={form.errors.mobile && form.touched.mobile}
                        // width={{ base: '100%', lg: '68%' }}
                        width="calc(100% - 81px)"
                        m={{ base: '0 0 18px 0', xl: '0' }}
                        // ml="2px"
                      >
                        <Input
                          {...field}
                          w="100%"
                          id="mobile"
                          variant="outline"
                          placeholder={fieldName.mobile}
                          fontFamily="'Geist',sans-serif"
                          fontSize="18px"
                          color="#28231E"
                          height={{ base: '48px', lg: '60px' }}
                          padding="10px"
                          borderRadius="0 8px 8px 0"
                          border="1px solid #D6D8D9"
                          borderLeftColor="transparent"
                          p="20px"
                          type="tell"
                          mt="32px"
                        />
                        <FormErrorMessage fontFamily="'Geist',sans-serif">
                          {form.errors.mobile}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </Box>
                <Field name="privacy">
                  {({ field, form }) => (
                    <FormControl
                      isInvalid={form.errors.privacy && form.touched.privacy}
                      isRequired
                      width="100%"
                      className="priv-check"
                    >
                      <FormLabel
                        fontFamily="'Geist',sans-serif"
                        fontSize="16px"
                        htmlFor="privacyCheckbox"
                        display="flex"
                        alignItems="baseline"
                      >
                        <Checkbox
                          checked={values.privacy}
                          {...field}
                          id="privacy"
                          // name="privacyCheckbox"
                          variant="outline"
                          placeholder={fieldName.privacy}
                          margin="0 10px 0 0"
                          borderColor="#EC1C2D"
                          colorScheme="red"
                          alignItems="baseline"
                        >
                          <StructuredTextParser
                            str={render(fieldName.privacy)}
                            fontFamily="'Geist',sans-serif"
                            fontSize="18px"
                          />
                        </Checkbox>
                      </FormLabel>
                      <FormErrorMessage fontFamily="'Geist',sans-serif">
                        {form.errors.privacy}
                      </FormErrorMessage>
                      <style>
                        {`
                          .priv-check a {
                            color: #EC1C2D;
                            text-decoration: underline;
                          }`}
                      </style>
                    </FormControl>
                  )}
                </Field>
              </Box>

              <Box
                display="flex"
                ref={ref}
                justifyContent="center"
                my={{ base: '30px', lg: '60px' }}
                w={{ base: '90%', lg: '55%' }}
                mx="auto"
              >
                <Button
                  type="submit"
                  w="full"
                  isLoading={isLoading}
                  fontSize={{
                    base: '16px',
                    lg: '18px',
                  }}
                  isDisabled={!isValid || !dirty || isLoading}
                  fontFamily="'Geist',sans-serif"
                  _active={{
                    border: 'none',
                    background: '#EC1C2D',
                  }}
                  margin="0 auto"
                  mb="5px"
                  bgColor="#EC1C2D"
                  h={'auto'}
                  minH="56px"
                  textAlign="left"
                  borderRadius="55px"
                  color="#faf7f0"
                  textDecoration="none"
                  _hover={{
                    bg: '#EC1C2D',
                  }}
                >
                  {fieldName.submitBtn || 'Submit'}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      )}

      {showResp && (
        <Box
          display="flex"
          flexDir="column"
          justifyContent="center"
          alignItems="center"
          gap="10px"
          m={{ base: '80px auto', md: '140px 0' }}
          maxW="530px"
        >
          <Box
            fontSize={{ base: '30px', lg: '48px' }}
            fontFamily="'Trirong', serif;"
            color="#28231E"
            px={{ base: '40px', lg: '10px' }}
            textAlign="center"
            lineHeight="50px"
            pb={{ base: '10px', md: '20px' }}
            as="h2"
            maxW={460}
            mx="auto"
            fontWeight={500}
          >
            {fieldName.addedToWaitlist}
          </Box>
          <Box
            fontFamily="'Geist',sans-serif"
            fontSize={{ base: '16px', lg: '20px' }}
            color="#28231E"
            px={{ base: '0', md: '40px', lg: '10px' }}
            textAlign="center"
            maxW={{ base: 240, md: 'none' }}
            mx="auto"
          >
            {fieldName?.appLive}
          </Box>
          <Box
            borderBottom="1px solid #28231E"
            w="full"
            opacity={0.2}
            my={{ base: '30px', md: '25px' }}
          />

          <Box
            fontSize={{ base: '20px', lg: '24px' }}
            fontFamily="'Trirong', serif;"
            color="#28231E"
            mt="5px"
            px={{ base: '0px', md: '40px', lg: '10px' }}
            textAlign="center"
            maxW={{ base: 246, md: 'none' }}
            mx="auto"
          >
            {fieldName.inviteFriend}
          </Box>

          <Button
            as={Link}
            href={`https://wa.me/?text=${fieldName?.shareWithFriendsLink}`}
            target="_blank"
            w={{ base: 'auto', md: 'auto', lg: '60%' }}
            fontSize={{
              base: '16px',
              lg: '18px',
            }}
            fontFamily="'Geist',sans-serif"
            _active={{
              border: 'none',
              background: '#00A171',
            }}
            mt={{ base: '10px', md: '15px' }}
            bgColor="#00A171"
            textAlign="left"
            borderRadius="55px"
            h="48px"
            color="#faf7f0"
            textDecoration="none"
            px="5px"
            _hover={{
              bg: '#00A171',
              textDecoration: 'none',
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              w="full"
            >
              <Box as="span" py="10px" px="10px" ml="10px">
                {fieldName.shareWithFriends || ''}
              </Box>

              <Box display="flex" borderLeft="1px solid #3BC79D" p="8px">
                <Image
                  src="https://static.sadhguru.org/d/46272/1731084049-whatsapp.png"
                  alt="whatsapp"
                  width="30px"
                  height="30px"
                />
              </Box>
            </Box>
          </Button>
        </Box>
      )}
      <style jsx global>{`
        ::placeholder {
          color: #a1a1a1;
        }
      `}</style>
    </Flex>
  );
};

export default MomForm;
